import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/adminService';
import { Search, Filter, RefreshCw, Eye, Calendar, DollarSign, TrendingUp, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';

const Complaints = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedAdvisor, setSelectedAdvisor] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const { data: complaintsData, isLoading, refetch } = useQuery({
    queryKey: ['complaints', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_service_reports_data');
      if (error) {
        console.error('Error loading complaints:', error);
        return [];
      }

      const allRows = data || [];
      const filtered = allRows.filter((r) => {
        const reqDate = new Date(r.complaint_registered_at || r.requested_at || r.created_at).toISOString().split('T')[0];
        return reqDate >= dateRange.startDate && reqDate <= dateRange.endDate;
      });

      return filtered;
    }
  });

  const [advisors, setAdvisors] = useState([]);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    if (complaintsData) {
      const uniqueAdvisors = [...new Set(complaintsData.map(c => c.assigned_advisor_name).filter(Boolean))];
      const uniqueSources = [...new Set(complaintsData.map(c => c.source).filter(Boolean))];
      setAdvisors(uniqueAdvisors);
      setSources(uniqueSources);
    }
  }, [complaintsData]);

  const filteredComplaints = (complaintsData || []).filter(c => {
    const matchesSearch = 
      (c.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.contact_number || '').includes(searchQuery) ||
      (c.service_type || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.id || '').toString().includes(searchQuery);

    const matchesStatus = selectedStatus === 'all' || 
      String(c.request_status || c.complaint_status || '').toLowerCase() === selectedStatus.toLowerCase();
    
    const matchesSource = selectedSource === 'all' || c.source === selectedSource;
    
    const matchesPriority = selectedPriority === 'all' || 
      String(c.priority || '').toLowerCase() === selectedPriority.toLowerCase();
    
    const matchesAdvisor = selectedAdvisor === 'all' || c.assigned_advisor_name === selectedAdvisor;

    return matchesSearch && matchesStatus && matchesSource && matchesPriority && matchesAdvisor;
  });

  const financialMetrics = filteredComplaints.reduce((acc, c) => {
    const quoted = Number(c.customer_quoted_amount) || 0;
    const mechanic = Number(c.payment_mechanic) || 0;
    acc.totalPaymentReceived += quoted;
    acc.totalPaymentToMechanic += mechanic;
    acc.totalProfit += quoted - mechanic;
    if (quoted > 0 || mechanic > 0) acc.complaintsWithPayment++;
    return acc;
  }, { totalPaymentReceived: 0, totalPaymentToMechanic: 0, totalProfit: 0, complaintsWithPayment: 0 });

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return '#10b981';
    if (s === 'cancelled') return '#ef4444';
    if (s.includes('progress') || s === 'assigned') return '#3b82f6';
    if (s === 'pending') return '#f59e0b';
    return '#6b7280';
  };

  const formatCurrency = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading complaints...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Complaints Management</h1>
          <p className="text-gray-600 mt-1">Manage and track service complaints</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={20} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search complaints..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Filter size={20} />
          <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
        </button>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="assigned">Assigned</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        )}
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Customer Quoted</span>
            <DollarSign size={20} className="text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(financialMetrics.totalPaymentReceived)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Mechanic Payment</span>
            <TrendingUp size={20} className="text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(financialMetrics.totalPaymentToMechanic)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Company Profit</span>
            <CheckCircle size={20} className="text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(financialMetrics.totalProfit)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">With Payment</span>
            <AlertCircle size={20} className="text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {financialMetrics.complaintsWithPayment}
          </p>
        </div>
      </div>

      {/* Complaints List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advisor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Profit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredComplaints.map((complaint) => (
                <tr key={complaint.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{complaint.customer_name || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{complaint.contact_number || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {complaint.service_type || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="px-2 py-1 text-xs font-medium rounded text-white"
                      style={{ backgroundColor: getStatusColor(complaint.request_status) }}
                    >
                      {complaint.request_status || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {complaint.assigned_advisor_name || 'Unassigned'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(complaint.customer_quoted_amount || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    {formatCurrency((Number(complaint.customer_quoted_amount) || 0) - (Number(complaint.payment_mechanic) || 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(complaint.complaint_registered_at || complaint.requested_at || complaint.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => navigate(`/complaints/${complaint.id}`)}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredComplaints.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No complaints found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Complaints;
